/**
 * Rastyle Club OS™ — Bashment Reflex (Phase 3 Gaming Room)
 *
 * Rhythm-tap game powered by the Web Audio API (no licensed audio required).
 * - A 4-on-the-floor synth beat at 110 BPM drives the timing.
 * - Notes spawn at the top and fall toward a "hit zone" near the bottom.
 * - Tap (touch / mouse / Space / D F J K keys) when the note lands.
 * - Timing windows: PERFECT (±90ms, +30pts) · GOOD (±180ms, +15pts) · MISS (0pts, combo break)
 * - Round = 60s. Score + perfect/good/miss counts posted to backend.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Zap, Loader2, Music, RotateCcw, Trophy, Volume2, VolumeX, Play, Flame
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ---- Timing config -----------------------------------------------------
const BPM = 110;
const SEC_PER_BEAT = 60 / BPM;            // 0.545s
const ROUND_SEC = 60;
const TRAVEL_MS = 1800;                   // how long a note takes from top to hit zone
const PERFECT_WINDOW_MS = 90;
const GOOD_WINDOW_MS = 180;
const SCORE_PERFECT = 30;
const SCORE_GOOD = 15;
const LANES = ['D', 'F', 'J', 'K'];       // 4 lanes mapped to keyboard
const LANE_COLORS = [
  'from-rastyle-orange to-red-600',
  'from-fomo-amber to-rastyle-orange',
  'from-euphoria-pink to-rastyle-orange',
  'from-fomo-amber to-euphoria-pink',
];

// Deterministic pattern generator — feels musical, never identical twice.
const buildPattern = (durationSec) => {
  // One note every half-beat at the offsets that match the kick/snare pulses.
  const notes = [];
  const steps = Math.floor(durationSec / (SEC_PER_BEAT / 2));
  for (let i = 4; i < steps; i++) {            // 2-beat lead-in silent
    // Rhythm rule: every beat = kick (lane 0), every half-off-beat = snare (lane 2),
    // hat (lanes 1/3) sprinkled every 3 steps for syncopation.
    const t = i * (SEC_PER_BEAT / 2) * 1000;   // ms from round start
    if (i % 2 === 0) notes.push({ t, lane: 0 });
    else if (i % 4 === 1) notes.push({ t, lane: 2 });
    if (i % 3 === 0) notes.push({ t, lane: 1 + (i % 2) * 2 }); // 1 or 3
  }
  return notes;
};

// ---- Web Audio synth ---------------------------------------------------
class BeatEngine {
  constructor() {
    this.ctx = null;
    this.muted = false;
  }
  ensure() {
    if (!this.ctx) {
      try {
        this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      } catch { /* no audio context — silent mode */ }
    }
    return this.ctx;
  }
  kick(when) {
    const ctx = this.ensure(); if (!ctx || this.muted) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.setValueAtTime(120, when);
    osc.frequency.exponentialRampToValueAtTime(40, when + 0.18);
    gain.gain.setValueAtTime(0.6, when);
    gain.gain.exponentialRampToValueAtTime(0.01, when + 0.25);
    osc.connect(gain).connect(ctx.destination);
    osc.start(when); osc.stop(when + 0.28);
  }
  snare(when) {
    const ctx = this.ensure(); if (!ctx || this.muted) return;
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.18, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const src = ctx.createBufferSource();
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.35, when);
    gain.gain.exponentialRampToValueAtTime(0.01, when + 0.18);
    src.buffer = buf;
    src.connect(gain).connect(ctx.destination);
    src.start(when); src.stop(when + 0.18);
  }
  hat(when) {
    const ctx = this.ensure(); if (!ctx || this.muted) return;
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.05, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.6;
    const src = ctx.createBufferSource();
    const filt = ctx.createBiquadFilter();
    filt.type = 'highpass';
    filt.frequency.value = 7000;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.18, when);
    gain.gain.exponentialRampToValueAtTime(0.001, when + 0.05);
    src.buffer = buf;
    src.connect(filt).connect(gain).connect(ctx.destination);
    src.start(when); src.stop(when + 0.05);
  }
  click(when) {
    const ctx = this.ensure(); if (!ctx || this.muted) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.setValueAtTime(1200, when);
    gain.gain.setValueAtTime(0.18, when);
    gain.gain.exponentialRampToValueAtTime(0.001, when + 0.05);
    osc.connect(gain).connect(ctx.destination);
    osc.start(when); osc.stop(when + 0.06);
  }
  hit(lane) {
    const ctx = this.ensure(); if (!ctx) return;
    const now = ctx.currentTime;
    if (lane === 0) this.kick(now);
    else if (lane === 2) this.snare(now);
    else this.hat(now);
  }
}

const judgeHit = (deltaMs) => {
  const ad = Math.abs(deltaMs);
  if (ad <= PERFECT_WINDOW_MS) return 'perfect';
  if (ad <= GOOD_WINDOW_MS) return 'good';
  return 'miss';
};

// Last-run ghost storage — keyed per voter so signed-in users carry their
// personal-best across sessions. Anonymous players use a stable localStorage key.
const GHOST_STORAGE_KEY = (uid) => `rastyle_reflex_ghost_${uid || 'anon'}`;
const loadGhost = (uid) => {
  try {
    const raw = localStorage.getItem(GHOST_STORAGE_KEY(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.hits)) return null;
    return parsed; // { score, hits: [{t, lane, verdict}], saved_at }
  } catch { return null; }
};
const saveGhost = (uid, payload) => {
  try { localStorage.setItem(GHOST_STORAGE_KEY(uid), JSON.stringify(payload)); }
  catch { /* localStorage full / disabled — ignore */ }
};

const BashmentReflexGame = ({ onClose, userId }) => {
  const [phase, setPhase] = useState('intro'); // intro | playing | gameover
  const [timeLeft, setTimeLeft] = useState(ROUND_SEC);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [counts, setCounts] = useState({ perfect: 0, good: 0, miss: 0 });
  const [muted, setMuted] = useState(false);
  const [flash, setFlash] = useState(null); // 'perfect' | 'good' | 'miss'
  const [submitting, setSubmitting] = useState(false);
  const [finalSummary, setFinalSummary] = useState(null);
  const [ghost, setGhost] = useState(() => loadGhost(userId));

  // Live falling notes — mutable for perf
  const fallingRef = useRef([]); // [{id, lane, t}]
  const [, setNoteTick] = useState(0);   // re-render trigger
  const startTimeRef = useRef(null);
  const patternRef = useRef([]);
  const nextSpawnIdxRef = useRef(0);
  const engineRef = useRef(new BeatEngine());
  const rafRef = useRef(null);

  // Hits recorder for the next-run ghost
  const recordedHitsRef = useRef([]); // [{t, lane, verdict}]

  // ---- start / stop helpers ----
  const reset = () => {
    setPhase('intro');
    setTimeLeft(ROUND_SEC);
    setScore(0);
    setCombo(0);
    setMaxCombo(0);
    setCounts({ perfect: 0, good: 0, miss: 0 });
    setFinalSummary(null);
    fallingRef.current = [];
    nextSpawnIdxRef.current = 0;
    recordedHitsRef.current = [];
  };

  const start = () => {
    reset();
    engineRef.current.muted = muted;
    engineRef.current.ensure();
    patternRef.current = buildPattern(ROUND_SEC);
    startTimeRef.current = performance.now();
    setPhase('playing');
  };

  // Submit score when game ends
  const finishRound = useCallback(async (finalCounts, finalScore, finalMaxCombo) => {
    setSubmitting(true);

    // Persist the run as the new "ghost" for the next attempt ONLY if it
    // beats the existing personal best (or no ghost yet). This way the user
    // is always chasing their best run, not their last one.
    try {
      const prev = loadGhost(userId);
      if (!prev || finalScore > (prev.score || 0)) {
        const payload = {
          score: finalScore,
          hits: recordedHitsRef.current.slice(),
          saved_at: new Date().toISOString(),
        };
        saveGhost(userId, payload);
        setGhost(payload);
      }
    } catch { /* ignore */ }

    try {
      const res = await axios.post(`${API}/api/games/reflex/submit`, {
        score: finalScore,
        perfect_count: finalCounts.perfect,
        good_count: finalCounts.good,
        miss_count: finalCounts.miss,
        max_combo: finalMaxCombo,
        duration_ms: ROUND_SEC * 1000,
      }, { withCredentials: true });
      setFinalSummary(res.data);
      if (res.data.is_personal_best) {
        toast.success(`🔥 New personal best — ${finalScore} pts`);
      }
    } catch (e) {
      setFinalSummary({
        score: finalScore,
        perfect_count: finalCounts.perfect,
        good_count: finalCounts.good,
        miss_count: finalCounts.miss,
        max_combo: finalMaxCombo,
        is_personal_best: false,
        saved: false,
      });
    } finally {
      setSubmitting(false);
      setPhase('gameover');
    }
  }, [userId]);

  // Main game loop
  useEffect(() => {
    if (phase !== 'playing') return undefined;
    let rafId;
    const loop = () => {
      const elapsed = performance.now() - startTimeRef.current;
      const tElapsedMs = elapsed;

      // Spawn notes whose hit-time minus travel-time is now-or-past
      while (nextSpawnIdxRef.current < patternRef.current.length) {
        const n = patternRef.current[nextSpawnIdxRef.current];
        if (n.t - TRAVEL_MS <= tElapsedMs) {
          fallingRef.current.push({ id: `${nextSpawnIdxRef.current}-${n.lane}`, lane: n.lane, t: n.t });
          nextSpawnIdxRef.current += 1;
        } else break;
      }

      // Drop notes that flew past the miss window
      const newFalling = [];
      let missAdded = 0;
      for (const n of fallingRef.current) {
        if (n.t < tElapsedMs - GOOD_WINDOW_MS) {
          missAdded += 1;
        } else newFalling.push(n);
      }
      fallingRef.current = newFalling;
      if (missAdded) {
        setCounts((c) => ({ ...c, miss: c.miss + missAdded }));
        setCombo(0);
        setFlash('miss');
      }

      // Tick timeLeft + force render
      const remaining = Math.max(0, Math.ceil(ROUND_SEC - tElapsedMs / 1000));
      setTimeLeft((cur) => (cur === remaining ? cur : remaining));
      setNoteTick((k) => k + 1);

      if (tElapsedMs >= ROUND_SEC * 1000) {
        // End round — use latest state via setters
        setCounts((c) => {
          setMaxCombo((mc) => {
            // Capture current score via stale closure — use ref via setScore callback
            setScore((s) => {
              finishRound(c, s, mc);
              return s;
            });
            return mc;
          });
          return c;
        });
        return;
      }
      rafId = requestAnimationFrame(loop);
      rafRef.current = rafId;
    };
    rafId = requestAnimationFrame(loop);
    rafRef.current = rafId;
    return () => cancelAnimationFrame(rafId);
  }, [phase, finishRound]);

  // Flash clear
  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), 380);
    return () => clearTimeout(t);
  }, [flash]);

  // ---- INPUT HANDLING ----
  const hitLane = useCallback((lane) => {
    if (phase !== 'playing') return;
    const tElapsedMs = performance.now() - startTimeRef.current;

    // Find the nearest unresolved note in this lane within timing window
    let bestIdx = -1;
    let bestAbs = Infinity;
    fallingRef.current.forEach((n, i) => {
      if (n.lane !== lane) return;
      const d = Math.abs(n.t - tElapsedMs);
      if (d < bestAbs && d <= GOOD_WINDOW_MS) {
        bestAbs = d;
        bestIdx = i;
      }
    });

    if (bestIdx < 0) {
      // Stray tap → tiny combo penalty + miss flash but no score deduction
      setCombo(0);
      setFlash('miss');
      return;
    }

    const target = fallingRef.current[bestIdx];
    const verdict = judgeHit(target.t - tElapsedMs);
    fallingRef.current.splice(bestIdx, 1); // resolved
    engineRef.current.hit(lane);

    // Record for ghost replay (only successful hits — misses don't replay)
    if (verdict === 'perfect' || verdict === 'good') {
      recordedHitsRef.current.push({ t: target.t, lane, verdict });
    }

    if (verdict === 'perfect') {
      setScore((s) => s + SCORE_PERFECT);
      setCounts((c) => ({ ...c, perfect: c.perfect + 1 }));
      setCombo((cc) => {
        const next = cc + 1;
        setMaxCombo((m) => (next > m ? next : m));
        return next;
      });
      setFlash('perfect');
    } else if (verdict === 'good') {
      setScore((s) => s + SCORE_GOOD);
      setCounts((c) => ({ ...c, good: c.good + 1 }));
      setCombo((cc) => {
        const next = cc + 1;
        setMaxCombo((m) => (next > m ? next : m));
        return next;
      });
      setFlash('good');
    } else {
      setCounts((c) => ({ ...c, miss: c.miss + 1 }));
      setCombo(0);
      setFlash('miss');
    }
  }, [phase]);

  // Keyboard listener
  useEffect(() => {
    if (phase !== 'playing') return undefined;
    const onKey = (e) => {
      if (e.repeat) return;
      const k = e.key.toUpperCase();
      const idx = LANES.indexOf(k);
      if (idx >= 0) {
        e.preventDefault();
        hitLane(idx);
      }
      if (k === ' ') {
        e.preventDefault();
        // Space hits lane 0 (kick)
        hitLane(0);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, hitLane]);

  // Cleanup
  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (engineRef.current.ctx) engineRef.current.ctx.close().catch(() => {});
  }, []);

  // ===== Render =====
  if (phase === 'intro') {
    return (
      <div className="bg-zinc-900 border border-white/10 rounded-2xl p-6 sm:p-8 text-center" data-testid="reflex-intro">
        <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-pink-600 flex items-center justify-center mb-4">
          <Zap className="w-8 h-8 text-white" />
        </div>
        <h2 className="font-heading font-extrabold text-3xl uppercase tracking-tight mb-1">Bashment Reflex</h2>
        <p className="text-zinc-400 text-sm sm:text-base mb-6 max-w-md mx-auto">
          Tap each note as it hits the line. Perfect timing scores big. 60 seconds to shell di place.
        </p>

        <div className="grid sm:grid-cols-3 gap-3 max-w-md mx-auto mb-6 text-left text-xs">
          <div className="bg-white/5 border border-rastyle-orange/30 rounded-xl p-3">
            <p className="font-mono font-bold text-rastyle-orange text-lg">+30</p>
            <p className="text-zinc-400">Perfect (±90ms)</p>
          </div>
          <div className="bg-white/5 border border-fomo-amber/30 rounded-xl p-3">
            <p className="font-mono font-bold text-fomo-amber text-lg">+15</p>
            <p className="text-zinc-400">Good (±180ms)</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-3">
            <p className="font-mono font-bold text-zinc-500 text-lg">0</p>
            <p className="text-zinc-400">Miss · combo reset</p>
          </div>
        </div>

        <div className="text-xs text-zinc-500 mb-6">
          Keyboard: <span className="font-mono text-rastyle-orange">D F J K</span> · or just tap the lanes on mobile
        </div>

        {ghost && (
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 bg-fomo-amber/15 border border-fomo-amber/40 text-fomo-amber font-mono text-[11px] uppercase tracking-wider rounded-full"
            data-testid="reflex-ghost-indicator"
          >
            <Flame className="w-3.5 h-3.5" /> Personal Best · {ghost.score} pts — beat your ghost
          </div>
        )}

        <div>
          <button
            onClick={start}
            className="px-6 py-3 bg-rastyle-orange text-black font-bold uppercase tracking-wide rounded-full inline-flex items-center gap-2 hover:bg-orange-400 transition-colors"
            data-testid="reflex-start-btn"
          >
            <Play className="w-4 h-4" /> Start Round
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'gameover' && finalSummary) {
    return (
      <div className="bg-gradient-to-br from-red-500 via-rastyle-orange to-fomo-amber p-1 rounded-2xl" data-testid="reflex-gameover">
        <div className="bg-black rounded-2xl p-8 text-center">
          <Trophy className={`w-16 h-16 mx-auto mb-3 ${finalSummary.is_personal_best ? 'text-fomo-amber' : 'text-rastyle-orange'}`} />
          <h2 className="font-heading font-extrabold text-3xl uppercase tracking-tight mb-1">
            {finalSummary.is_personal_best ? 'Personal Best!' : 'Round Complete'}
          </h2>
          <p className="text-zinc-400 text-sm mb-6">
            {finalSummary.saved ? 'Score saved to leaderboard.' : 'Sign in to save your scores to the leaderboard.'}
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-md mx-auto mb-6">
            <div className="p-3 bg-white/5 border border-white/10 rounded-xl">
              <p className="font-mono font-bold text-rastyle-orange text-2xl">{finalSummary.score}</p>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Score</p>
            </div>
            <div className="p-3 bg-white/5 border border-rastyle-orange/20 rounded-xl">
              <p className="font-mono font-bold text-rastyle-orange text-2xl">{finalSummary.perfect_count}</p>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Perfect</p>
            </div>
            <div className="p-3 bg-white/5 border border-fomo-amber/20 rounded-xl">
              <p className="font-mono font-bold text-fomo-amber text-2xl">{finalSummary.good_count}</p>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Good</p>
            </div>
            <div className="p-3 bg-white/5 border border-white/10 rounded-xl">
              <p className="font-mono font-bold text-euphoria-pink text-2xl">{finalSummary.max_combo}</p>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Max Combo</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={start}
              className="px-6 py-3 bg-rastyle-orange text-black font-bold uppercase tracking-wide rounded-full inline-flex items-center justify-center gap-2 hover:bg-orange-400 transition-colors"
              data-testid="reflex-play-again"
            >
              <RotateCcw className="w-4 h-4" /> Play Again
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 border border-white/20 text-white font-medium rounded-full hover:border-rastyle-orange transition-colors"
            >
              Back to Room
            </button>
          </div>
        </div>
      </div>
    );
  }

  // playing
  return (
    <div className="bg-zinc-950 border border-white/10 rounded-2xl overflow-hidden" data-testid="reflex-playing">
      {/* HUD */}
      <div className="px-4 py-3 bg-black border-b border-white/10 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Time</span>
          <span className="font-mono font-bold text-2xl text-rastyle-orange">{timeLeft}s</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Score</span>
          <span className="font-mono font-bold text-2xl">{score}</span>
          {combo >= 5 && (
            <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-fomo-amber px-2 py-0.5 bg-fomo-amber/15 border border-fomo-amber/40 rounded-full">
              <Flame className="w-3.5 h-3.5" /> ×{combo}
            </span>
          )}
          {ghost && (
            <span
              className={`inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${
                score >= ghost.score
                  ? 'bg-mpesa-green/15 border border-mpesa-green/40 text-mpesa-green'
                  : 'bg-white/5 border border-white/10 text-zinc-400'
              }`}
              data-testid="reflex-pb-indicator"
            >
              PB {ghost.score} · {score >= ghost.score ? 'AHEAD' : `−${ghost.score - score}`}
            </span>
          )}
        </div>
        <button
          onClick={() => { setMuted((m) => { engineRef.current.muted = !m; return !m; }); }}
          className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
          aria-label="Toggle sound"
          data-testid="reflex-mute-toggle"
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>

      {/* Lane field */}
      <div className="relative w-full h-[420px] sm:h-[480px] bg-gradient-to-b from-zinc-950 via-zinc-900 to-black overflow-hidden select-none">
        <div className="absolute inset-0 grid grid-cols-4">
          {LANES.map((label, idx) => (
            <button
              key={label}
              type="button"
              onPointerDown={(e) => { e.preventDefault(); hitLane(idx); }}
              className="relative border-r border-white/5 last:border-r-0 active:bg-white/[0.04]"
              data-testid={`reflex-lane-${idx}`}
              aria-label={`Lane ${label}`}
            >
              {/* Falling notes */}
              <AnimatePresence>
                {fallingRef.current.filter((n) => n.lane === idx).map((n) => {
                  const tElapsedMs = performance.now() - startTimeRef.current;
                  const progress = 1 - (n.t - tElapsedMs) / TRAVEL_MS;
                  const top = Math.max(0, Math.min(100, progress * 100));
                  return (
                    <motion.div
                      key={n.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className={`absolute left-1/2 -translate-x-1/2 w-[70%] h-12 rounded-xl bg-gradient-to-br ${LANE_COLORS[idx]} shadow-[0_0_15px_rgba(255,116,0,0.4)]`}
                      style={{ top: `${top}%`, transition: 'top 16ms linear' }}
                    />
                  );
                })}
              </AnimatePresence>

              {/* Ghost notes — previous personal-best run */}
              {ghost?.hits && ghost.hits.filter((g) => g.lane === idx).map((g, i) => {
                const tElapsedMs = performance.now() - startTimeRef.current;
                // Show only ghosts near the current playhead so the field doesn't get cluttered
                if (Math.abs(g.t - tElapsedMs) > TRAVEL_MS) return null;
                const progress = 1 - (g.t - tElapsedMs) / TRAVEL_MS;
                const top = Math.max(0, Math.min(100, progress * 100));
                return (
                  <div
                    key={`ghost-${idx}-${i}`}
                    aria-hidden="true"
                    className="absolute left-1/2 -translate-x-1/2 w-[55%] h-10 rounded-xl border-2 border-dashed border-white/15"
                    style={{ top: `${top}%`, transition: 'top 16ms linear' }}
                    data-testid={`reflex-ghost-${idx}-${i}`}
                  />
                );
              })}

              {/* Lane label at bottom */}
              <div className="absolute bottom-2 inset-x-0 text-center font-mono font-bold text-xs text-zinc-500">
                {label}
              </div>
            </button>
          ))}
        </div>

        {/* Hit line */}
        <div className="absolute left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-rastyle-orange to-transparent shadow-[0_0_15px_rgba(255,116,0,0.6)]" style={{ top: '85%' }} />

        {/* Flash */}
        <AnimatePresence>
          {flash && (
            <motion.div
              key={flash + Math.random()}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-x-0 top-[55%] -translate-y-1/2 text-center pointer-events-none"
            >
              <span className={`font-heading font-black text-5xl uppercase tracking-tighter drop-shadow ${
                flash === 'perfect' ? 'text-rastyle-orange'
                  : flash === 'good' ? 'text-fomo-amber'
                  : 'text-zinc-500'
              }`}>
                {flash}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer hint */}
      <div className="px-4 py-2 bg-black border-t border-white/10 flex items-center justify-between text-xs text-zinc-500">
        <span className="font-mono">D · F · J · K</span>
        <span className="flex items-center gap-1.5">
          <Music className="w-3.5 h-3.5" /> {BPM} BPM · Perfect ±{PERFECT_WINDOW_MS}ms
        </span>
      </div>

      {submitting && (
        <div className="px-4 py-2 bg-rastyle-orange/10 border-t border-rastyle-orange/30 text-xs text-rastyle-orange flex items-center gap-2 justify-center">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving score...
        </div>
      )}
    </div>
  );
};

export default BashmentReflexGame;
